import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrainingPlanCard = ({ 
  plan, 
  onSelect, 
  isSelected = false,
  disabled = false 
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'Foundation': return 'text-secondary bg-secondary/10';
      case 'Strength': return 'text-primary bg-primary/10';
      case 'Endurance': return 'text-warning bg-warning/10';
      case 'Recovery': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'paused': return 'Pause';
      case 'not-started': return 'Play';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'in-progress': return 'text-primary';
      case 'paused': return 'text-warning';
      case 'not-started': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const formatLastCompleted = (date) => {
    if (!date) return 'Never completed';
    const now = new Date();
    const lastCompleted = new Date(date);
    const diffTime = Math.abs(now - lastCompleted);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return lastCompleted?.toLocaleDateString();
  };

  const handleCardClick = () => {
    if (!disabled && onSelect) {
      onSelect(plan);
    }
  };

  return (
    <div
      className={`
        relative bg-surface border-2 rounded-2xl p-8 transition-all duration-300 ease-out
        cursor-pointer touch-spacing-lg breathing-focus
        ${isSelected 
          ? 'border-primary bg-primary/5 shadow-lg' 
          : 'border-border hover:border-primary/50 hover:shadow-md'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${isHovered && !disabled ? 'transform scale-105' : ''}
      `}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={`Select training plan: ${plan?.name}`}
      aria-pressed={isSelected}
      aria-disabled={disabled}
    >
      {/* Resume Session Banner */}
      {plan?.canResume && (
        <div className="absolute -top-3 left-6 right-6 bg-warning text-warning-foreground px-4 py-2 rounded-lg text-center">
          <div className="flex items-center justify-center space-x-2">
            <Icon name="Clock" size={20} />
            <span className="font-semibold text-lg">Resume Available</span>
          </div>
        </div>
      )}
      {/* Plan Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <h3 className="text-3xl font-bold text-foreground mb-2 leading-tight">
            {plan?.name}
          </h3>
          <p className="text-lg text-muted-foreground mb-4">
            {plan?.description}
          </p>
        </div>
        
        <div className={`
          w-16 h-16 rounded-full flex items-center justify-center
          ${getStatusColor(plan?.status)} bg-current/10
        `}>
          <Icon 
            name={getStatusIcon(plan?.status)} 
            size={32} 
            className={getStatusColor(plan?.status)}
          />
        </div>
      </div>
      {/* Plan Details */}
      <div className="space-y-4 mb-6">
        {/* Current Phase */}
        <div className="flex items-center justify-between">
          <span className="text-xl text-foreground font-medium">Current Phase:</span>
          <span className={`
            px-4 py-2 rounded-full text-lg font-semibold
            ${getPhaseColor(plan?.currentPhase)}
          `}>
            {plan?.currentPhase}
          </span>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between">
          <span className="text-xl text-foreground font-medium">Progress:</span>
          <div className="flex items-center space-x-3">
            <div className="w-32 h-3 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${plan?.progressPercentage}%` }}
              />
            </div>
            <span className="text-lg font-semibold text-primary">
              {plan?.progressPercentage}%
            </span>
          </div>
        </div>

        {/* Exercise Count */}
        <div className="flex items-center justify-between">
          <span className="text-xl text-foreground font-medium">Exercises:</span>
          <span className="text-lg text-foreground">
            {plan?.completedExercises} / {plan?.totalExercises}
          </span>
        </div>

        {/* Last Completed */}
        <div className="flex items-center justify-between">
          <span className="text-xl text-foreground font-medium">Last Session:</span>
          <span className="text-lg text-muted-foreground">
            {formatLastCompleted(plan?.lastCompleted)}
          </span>
        </div>

        {/* Duration */}
        <div className="flex items-center justify-between">
          <span className="text-xl text-foreground font-medium">Duration:</span>
          <span className="text-lg text-foreground">
            ~{plan?.estimatedDuration} minutes
          </span>
        </div>
      </div>
      {/* Action Button */}
      <div className="pt-4 border-t border-border">
        <Button
          variant={plan?.canResume ? "warning" : "default"}
          size="xl"
          fullWidth
          iconName={plan?.canResume ? "Play" : "ArrowRight"}
          iconPosition="right"
          className="text-xl py-4"
          disabled={disabled}
        >
          {plan?.canResume ? "Resume Session" : "Start Training"}
        </Button>
      </div>
      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-4 right-4 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
          <Icon name="Check" size={20} color="white" />
        </div>
      )}
    </div>
  );
};

export default TrainingPlanCard;